import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { FC, KeyboardEvent, MouseEvent } from 'react';
import styled, { css } from 'styled-components';

import {
  AIRewrite,
  Avatar,
  Button,
  defaultThemeProp,
  Flex,
  getActiveElement,
  Icon,
  isInstance,
  Progress,
  useAfterInitialEffect,
  useElement,
  useI18n,
  useLiveLog,
  usePrevious
} from '@pega/cosmos-react-core';
import type { AIAction, ForwardProps } from '@pega/cosmos-react-core';
import { extractMentions, restoreMentionPlaceholders } from '@pega/cosmos-react-rte';
import type { RichTextEditorState } from '@pega/cosmos-react-rte';

import { FeedContext } from './Feed.context';
import FeedAttachments from './FeedAttachments';
import FeedButton from './FeedButton';
import FeedRichText from './FeedRichText';
import type { FeedInputRegionProps } from './Feed.types';

const StyledAvatar = styled(Avatar)(({ theme }) => {
  return css`
    margin-block-start: calc(0.5 * ${theme.base.spacing});
  `;
});

StyledAvatar.defaultProps = defaultThemeProp;

const StyledContent = styled.div`
  width: 100%;
  overflow-y: hidden;
  position: relative;
`;

export const StyledActions = styled.div`
  ${FeedButton} + ${FeedButton} {
    margin-inline-start: 0;
  }
`;

StyledActions.defaultProps = defaultThemeProp;

const HiddenFileInput = styled.input`
  display: none;
`;

const FeedInputRegion: FC<ForwardProps & FeedInputRegionProps> = (
  props: ForwardProps & FeedInputRegionProps
) => {
  const {
    defaultValue,
    variant,
    id,
    banners,
    progress,
    onRewriteClick,
    attachments = [],
    onFilesAdded,
    onSubmit,
    rteRef,
    recipients,
    postType,
    postTypeMenu,
    visibility,
    visibilityControl,
    onUnmount
  } = props;

  const t = useI18n();
  const { announcePolite } = useLiveLog();
  const { setShowSearchResults } = useContext(FeedContext);
  const { avatarSrc, fullname, username, userStatus } = useContext(FeedContext).userInfo;

  const [emptyText, setEmptyText] = useState(true);
  const [attachmentEl, setAttachmentEl] = useElement<HTMLInputElement>();
  const [aiButtonEl, setAIButtonEl] = useElement<HTMLButtonElement>();
  const [showAIPopover, setShowAIPopover] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [suggestion, setSuggestion] = useState<string | undefined>(undefined);
  const [hasSuggestionContent, setHasSuggestionContent] = useState(false);
  const suggestionRteRef = useRef<RichTextEditorState>(null);

  // Sync AI-generated suggestion into the suggestion FeedRichText whenever it changes
  useEffect(() => {
    if (suggestion !== undefined) {
      suggestionRteRef.current?.clear();
      suggestionRteRef.current?.insertMarkdown?.(suggestion);
      setHasSuggestionContent(true);
    } else {
      suggestionRteRef.current?.clear();
      setHasSuggestionContent(false);
    }
  }, [suggestion]);
  const prevAttachments = usePrevious(attachments);
  const suppressNextAnnouncement = useRef(false);
  const postButtonRef = useRef<HTMLButtonElement>(null);
  const isSubmitButtonClicked = useRef(false);
  const previouslyFocusedElementRef = useRef<Element | null>(null);
  const valueRef = useRef(defaultValue ?? '');

  const handleSubmit = () => {
    setShowSearchResults?.(false);

    if ((!emptyText || attachments.length > 0) && attachments.every(x => !x.error)) {
      onSubmit({
        id,
        value: rteRef.current?.getPlainText() || '',
        attachments: attachments || [],
        username,
        postType,
        visibility,
        recipients: variant === 'post' ? recipients : undefined,
        clear: () => {
          // Clear the RTE content and reset focus state
          rteRef.current?.clear();
          rteRef.current?.focus();
          isSubmitButtonClicked.current = false;
        }
      });
      suppressNextAnnouncement.current = true;
      isSubmitButtonClicked.current = true;
    }
  };

  const handleGenerate = (rewriteAction: AIAction, additionalInstructions: string) => {
    const originalContent = rteRef.current?.getPlainText() || '';
    if (!originalContent) return;

    const { cleanText, mentionMap, userPlaceholders } = extractMentions(originalContent);

    // Capture in closure so concurrent Generate calls don't corrupt each other's restore map
    const capturedMentionMap = mentionMap;
    const capturedUserPlaceholders = userPlaceholders;

    setIsGenerating(true);
    setSuggestion(undefined);

    onRewriteClick?.(
      cleanText,
      rewriteAction,
      additionalInstructions,
      (rewrittenText: string) => {
        const restoredText = restoreMentionPlaceholders(
          rewrittenText,
          capturedMentionMap,
          capturedUserPlaceholders
        );
        setIsGenerating(false);
        setSuggestion(restoredText.trim() ? restoredText : undefined);
      },
      false
    );
  };

  const handleApplyRewrite = () => {
    const editedText = suggestionRteRef.current?.getPlainText() ?? suggestion ?? '';
    rteRef.current?.insertMarkdown?.(editedText);
    valueRef.current = editedText;

    // Manually update state since programmatic updates might not trigger onChange
    setEmptyText(editedText.trim() === '');

    // Close popover and reset AI state
    setShowAIPopover(false);
    setSuggestion(undefined);

    // Return focus to RTE
    rteRef.current?.focus();
  };

  // If the API call fails, progress will flip back to false without calling the success callback
  // Clear the loading state so the Generate button re-enables
  useAfterInitialEffect(() => {
    if (!progress) setIsGenerating(false);
  }, [progress]);

  const onKeyDown = (event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') handleSubmit();
  };

  const inputLabel =
    postType?.label || t(variant === 'post' ? 'feed_new_post' : 'feed_new_comment');
  const submitLabel = t(variant === 'post' ? 'feed_new_post_submit' : 'feed_new_comment_submit');

  // Using layout effect to capture the focused element prior to editor taking focus.
  useLayoutEffect(() => {
    previouslyFocusedElementRef.current = getActiveElement();

    return () => {
      // For reply variant, restore focus to the element that was previously focused

      const el = previouslyFocusedElementRef.current;
      if (variant === 'reply' && isInstance(el, HTMLElement) && el.isConnected) {
        el.focus();
      }
    };
  }, []);

  useEffect(() => {
    if (banners && isSubmitButtonClicked.current && postButtonRef.current) {
      postButtonRef.current.focus();
      isSubmitButtonClicked.current = false;
    }
  }, [banners]);

  useAfterInitialEffect(() => {
    if (!prevAttachments) return;

    if (prevAttachments.length !== attachments.length && !suppressNextAnnouncement.current) {
      const difference = attachments.length - prevAttachments.length;
      const absoluteDifference = Math.abs(difference);
      announcePolite({
        message: t(
          `feed_attachments_${difference > 0 ? 'added' : 'removed'}`,
          [absoluteDifference],
          {
            count: absoluteDifference
          }
        )
      });
    }
    suppressNextAnnouncement.current = false;
  }, [attachments.length]);

  useEffect(() => {
    return () => {
      onUnmount?.(valueRef.current);
    };
  }, []);

  return (
    <>
      <Flex container={{ alignItems: 'start' }}>
        <StyledAvatar
          name={fullname}
          size={variant === 'post' ? 'l' : 'm'}
          imageSrc={avatarSrc}
          status={userStatus}
        />

        <Flex
          as={StyledContent}
          container={{
            direction: 'column',
            gap: variant === 'post' ? 0.5 : 0,
            pad: [0.5, 0.5, 0.5, 1]
          }}
          inert={progress ? '' : undefined}
        >
          {(!!visibilityControl || !!banners) && postTypeMenu}
          {banners}
          {visibilityControl}

          <FeedRichText
            defaultValue={defaultValue}
            label={!!postTypeMenu && !visibilityControl && !banners ? postTypeMenu : inputLabel}
            onChange={() => {
              setEmptyText(rteRef.current?.getPlainText().trim() === '');
              valueRef.current = rteRef.current?.getPlainText() ?? '';
            }}
            onKeyDown={onKeyDown}
            ref={rteRef}
          />

          <Progress
            visible={!!progress}
            focusOnVisible
            placement='local'
            message={typeof progress === 'string' ? progress : undefined}
          />
        </Flex>

        <Flex
          as={StyledActions}
          container={{ direction: 'column', gap: 0.25, pad: [0.75, 0, 0.75, 0.5] }}
          item={{ alignSelf: 'end' }}
        >
          {onFilesAdded && (
            <Button
              variant='simple'
              disabled={!!progress}
              as={FeedButton}
              onClick={() => {
                setShowSearchResults?.(false);
                attachmentEl?.click();
              }}
              icon
              label={t('feed_attach_files')}
            >
              <Icon name='paper-clip' />
            </Button>
          )}

          {/* AI Rewrite Button */}
          {onRewriteClick && (
            <Button
              variant='simple'
              disabled={emptyText || !!progress}
              as={FeedButton}
              onClick={() => setShowAIPopover(true)}
              icon
              label={t('ai_rewrite_title')}
              ref={setAIButtonEl}
            >
              <Icon name='polaris' />
            </Button>
          )}

          {!progress && (
            <Button
              as={FeedButton}
              disabled={
                (emptyText && attachments.length === 0) ||
                attachments.some(x => x.error) ||
                !!progress
              }
              variant='simple'
              onClick={handleSubmit}
              icon
              label={submitLabel}
              ref={postButtonRef}
            >
              <Icon name='send' />
            </Button>
          )}
        </Flex>

        <HiddenFileInput
          ref={setAttachmentEl}
          type='file'
          multiple
          tabIndex={-1}
          onChange={() => {
            if (attachmentEl?.files) {
              onFilesAdded?.(Array.from(attachmentEl?.files));
            }
          }}
          // Must trick input to believe there is no value when activated so that the same file may be added consecutively.
          onClick={(event: MouseEvent) => {
            (event.target as HTMLInputElement).value = '';
          }}
        />
      </Flex>

      <FeedAttachments variant={variant} attachments={attachments} disabled={!!progress} />

      {/* AI Rewrite Popover */}
      {aiButtonEl && showAIPopover && onRewriteClick && (
        <AIRewrite
          target={aiButtonEl}
          isGenerating={isGenerating}
          suggestion={hasSuggestionContent ? suggestion : undefined}
          onGenerate={handleGenerate}
          onSubmit={handleApplyRewrite}
          suggestionEditor={
            <FeedRichText
              ref={suggestionRteRef}
              label={t('ai_rewrite_result_label')}
              labelHidden
              disabled={isGenerating || !suggestion?.trim()}
              readOnly={false}
              onChange={() =>
                setHasSuggestionContent(
                  (suggestionRteRef.current?.getPlainText() ?? '').trim().length > 0
                )
              }
            />
          }
          onCancel={() => {
            setShowAIPopover(false);
            setSuggestion(undefined);
            setIsGenerating(false);
            aiButtonEl.focus();
          }}
        />
      )}
    </>
  );
};

export default FeedInputRegion;
